import { type SuppliersPageTaxJurisdictionListQuery } from '../../../../../../../generated';
import {
  type TaxJurisdictionListResult,
  type TaxJurisdictionRow,
  type TaxMoney,
  type TaxRateRow,
  type TaxRateValue,
} from '../types';

const DEFAULT_ERROR_MESSAGE = 'Failed to load tax jurisdictions.';

/** Placeholder for an absent value in the tables and the detail modal. */
export const ABSENT_VALUE = '–';

type TaxJurisdictionListResponse =
  SuppliersPageTaxJurisdictionListQuery['taxJurisdictionList'];

type TaxJurisdictionNode = Extract<
  TaxJurisdictionListResponse,
  { __typename: 'TaxJurisdictionListResult' }
>['edges'][number]['node'];

type TaxRateNode = TaxJurisdictionNode['rates'][number];

const CATEGORY_LABELS: Record<string, string> = {
  STANDARD: 'Standard',
  REDUCED: 'Reduced',
  ZERO: 'Zero',
  EXEMPT: 'Exempt',
  EMPTY_DEPOSIT: 'Empty deposit',
};

const BEHAVIOR_LABELS: Record<string, string> = {
  EXCLUSIVE: 'Exclusive (added on top)',
  INCLUSIVE: 'Inclusive (part of the price)',
};

/** Falls back to the raw enum value for a category the portal has yet to learn. */
export const taxRateCategoryLabel = (category: string): string =>
  CATEGORY_LABELS[category] ?? category;

export const taxBehaviorLabel = (behavior: string): string =>
  BEHAVIOR_LABELS[behavior] ?? behavior;

const mapRateValue = (rate: TaxRateNode): TaxRateValue => {
  if (rate.rateValue.__typename === 'FixedAmountTaxRate') {
    const money: TaxMoney = {
      amount: rate.rateValue.fixedAmount.amount,
      currency: rate.rateValue.fixedAmount.currency,
      decimalPlaces: rate.rateValue.fixedAmount.decimalPlaces,
    };
    return { kind: 'fixed', money };
  }

  return { kind: 'percentage', percentage: rate.rateValue.percentage.amount };
};

const mapRate = (rate: TaxRateNode): TaxRateRow => ({
  id: rate.id,
  code: rate.code ?? null,
  category: rate.category,
  label: rate.label ?? null,
  value: mapRateValue(rate),
  taxBehavior: rate.taxBehavior,
  appliesToFreeProducts: rate.appliesToFreeProducts,
  priority: rate.priority,
  validFrom: rate.validFrom ?? null,
  validTo: rate.validTo ?? null,
});

const mapJurisdiction = (node: TaxJurisdictionNode): TaxJurisdictionRow => ({
  id: node.id,
  code: node.code,
  countryCode: node.countryCode,
  subdivisionCode: node.subdivisionCode ?? null,
  rates: (node.rates ?? []).map(mapRate),
});

/**
 * Maps a `TaxJurisdictionListResponse` union into a domain result. Returns
 * `null` while there is no response (loading or skipped). The union has two
 * failure members and no NotFoundError, so both become `{ kind: 'error' }`.
 */
export const mapTaxJurisdictionListResponse = (
  response: TaxJurisdictionListResponse | null | undefined
): TaxJurisdictionListResult | null => {
  if (!response) {
    return null;
  }

  switch (response.__typename) {
    case 'TaxJurisdictionListResult':
      return {
        kind: 'success',
        page: {
          jurisdictions: (response.edges ?? []).map((edge) =>
            mapJurisdiction(edge.node)
          ),
          pageInfo: {
            hasNextPage: response.pageInfo?.hasNextPage ?? false,
            endCursor: response.pageInfo?.endCursor ?? '',
          },
        },
      };
    case 'InputValidationFailure':
    case 'AuthorizationFailure':
      return {
        kind: 'error',
        message: response.message ?? DEFAULT_ERROR_MESSAGE,
      };
    default:
      return { kind: 'error', message: DEFAULT_ERROR_MESSAGE };
  }
};

/**
 * Formats a rate value for display: `19%` for a percentage rate, and the
 * money amount with its currency for a fixed one. Falls back to
 * `<amount> <currency>` for a currency code `Intl` rejects.
 */
export const formatTaxRateValue = (value: TaxRateValue): string => {
  if (value.kind === 'percentage') {
    return `${value.percentage}%`;
  }

  const { amount, currency, decimalPlaces } = value.money;
  const numeric = amount / 10 ** decimalPlaces;

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(numeric);
  } catch {
    return `${numeric.toFixed(decimalPlaces)} ${currency}`;
  }
};

/** AWSDate strings are already yyyy-mm-dd, so an absent bound is the only case to handle. */
export const formatTaxRateDate = (date: string | null): string =>
  date ?? ABSENT_VALUE;
